import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Printer, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { applySfgCounts, extractSfgCounts } from "@/lib/api";

/* Re-upload an old SFG packing list → AI proposes No of SFGs per existing coil → review → save. */
export const SfgCountDialog = ({ pkg, onClose, onPrint }) => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [rows, setRows] = useState(null);
  const [found, setFound] = useState(0);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("read");
    try {
      const d = await extractSfgCounts(pkg.id, f);
      setFound(d.rows_found);
      setRows(d.coils.map((c) => ({ ...c, value: c.new_count ?? c.current_count })));
      setSaved(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read the packing list");
    } finally { setBusy(""); }
  };

  const setVal = (id, v) => setRows((rs) => rs.map((r) => (r.coil_id === id ? { ...r, value: v } : r)));
  const valid = (v) => /^\d+$/.test(String(v)) && Number(v) >= 1 && Number(v) <= 999;
  const changes = (rows || []).filter((r) => valid(r.value) && Number(r.value) !== r.current_count);
  const allValid = (rows || []).every((r) => valid(r.value));

  const save = async () => {
    setBusy("save");
    try {
      const r = await applySfgCounts(pkg.id, changes.map((c) => ({ coil_id: c.coil_id, sfg_count: Number(c.value) })));
      toast.success(`No of SFGs updated on ${r.updated} coil${r.updated === 1 ? "" : "s"}`);
      setRows((rs) => rs.map((x) => ({ ...x, current_count: Number(x.value) })));
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["coils"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const unmatched = (rows || []).filter((r) => !r.matched_by).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="sfg-count-dialog">
      <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-orange-700 font-bold">Update No of SFGs</p>
            <h3 className="text-xl font-bold text-slate-900">{pkg.supplier_name} · <span className="font-mono-data">{pkg.packaging_list_number}</span></h3>
            <p className="text-sm text-slate-500">Upload the same packing list again. Only No of SFGs changes — weights, IDs and status stay as they are.</p>
          </div>
          <button onClick={onClose} data-testid="sfg-count-close" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <input ref={ref} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg" className="hidden" onChange={pick} data-testid="sfg-count-file" />
        <Button variant="outline" onClick={() => ref.current?.click()} disabled={!!busy} data-testid="sfg-count-upload-btn">
          {busy === "read" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading with AI…</> : <><UploadCloud className="h-4 w-4 mr-2" /> {rows ? "Upload again" : "Upload packing list"}</>}
        </Button>

        {rows && (
          <>
            <p className="text-xs text-slate-600" data-testid="sfg-count-summary">
              {found} rows read from the file · {rows.length} SFGs on this list{unmatched > 0 && <span className="text-amber-700 font-semibold"> · {unmatched} couldn&apos;t be matched — type their number by hand</span>}
            </p>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] tracking-[0.15em] uppercase text-slate-500">
                <tr><th className="text-left p-2">SFG ID</th><th className="text-left p-2">Supplier coil</th><th className="text-left p-2">Size · Net</th><th className="text-left p-2">Matched by</th><th className="text-right p-2">Now</th><th className="text-right p-2">No of SFGs</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.coil_id} className="border-t border-slate-100" data-testid={`sfg-count-row-${r.coil_id}`}>
                    <td className="p-2 font-mono-data font-semibold">{r.coil_id}</td>
                    <td className="p-2 font-mono-data text-xs">{r.supplier_coil_id || "—"}</td>
                    <td className="p-2 text-xs text-slate-600">{r.thickness_mm} × {r.width_mm} · {r.weight_kg} kg</td>
                    <td className="p-2 text-xs">{r.matched_by || <span className="text-amber-700 font-semibold">not matched</span>}</td>
                    <td className="p-2 text-right font-mono-data">{r.current_count}</td>
                    <td className="p-2 text-right">
                      <input type="number" min="1" max="999" step="1" value={r.value} onChange={(e) => setVal(r.coil_id, e.target.value)} data-testid={`sfg-count-input-${r.coil_id}`}
                        className={`w-20 h-8 px-2 text-right font-mono-data border rounded-sm ${!valid(r.value) ? "border-red-500" : Number(r.value) !== r.current_count ? "border-orange-500 bg-orange-50" : "border-slate-300"}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2">
              {saved && (
                <Button variant="outline" onClick={onPrint} data-testid="sfg-count-reprint-btn"><Printer className="h-4 w-4 mr-2" /> Reprint labels</Button>
              )}
              <Button onClick={save} disabled={!changes.length || !allValid || !!busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sfg-count-save-btn">
                {busy === "save" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save {changes.length || ""} change{changes.length === 1 ? "" : "s"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
